import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {OAuthService} from "angular-oauth2-oidc";
import { environment } from 'src/environments/environment';
import { UserRole } from 'src/app/models/user-roles.enum';
import { rolePermissions } from 'src/app/models/role-permissions';
import { Profile } from './models/ccm/profile.model';
import { Store } from '@ngrx/store';
import * as ReviewPageActions from '../app/views/ccm/state/review-page.actions';
import { distinctUntilChanged, map, Observable, shareReplay, startWith } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly rolesBlockedFromChartQueue = new Set<UserRole>([UserRole.TapestryAdmin]);

  private _validUser = true;
  private _isLoggingOut = false;
  private refreshTokenPromise?: Promise<string>;
  private apiUrl = environment.backEndUrl;
  private _profile?: Profile;


  public userRole: UserRole;
  public userPermissions: string[] = [];
  public readonly authState$: Observable<boolean> = this.oauthService.events.pipe(
    map(() => this.oauthService.hasValidAccessToken()),
    startWith(this.oauthService.hasValidAccessToken()),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );


  constructor(public oauthService: OAuthService, private http: HttpClient, private store: Store) {}

  public login() {
    this.oauthService.initCodeFlow();
  }

  public logout() {
    // Prevent multiple logout calls
    if (this._isLoggingOut) {
      console.log('Logout already in progress, skipping...');
      return;
    }

    this._isLoggingOut = true;

    try {
      this.oauthService.logOut();
      localStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this._isLoggingOut = false;
    }
  }

  public refreshToken(): Promise<string> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    // Use the actual refresh_token grant (HTTP POST to /token endpoint).
    // This is reliable after idle periods because it does not rely on an iframe
    // or SSO session cookie. silentRefresh() (iframe-based) fails with
    // "silent_refresh_timeout" when the browser throttles inactive tabs or
    // SameSite cookie policies block the iframe, so we avoid it here.
    this.refreshTokenPromise = this.oauthService.refreshToken()
      .then(() => {
        const refreshedToken = this.oauthService.getAccessToken();
        if (!refreshedToken) {
          throw new Error('Token refresh completed but access token is missing.');
        }
        return refreshedToken;
      })
      .finally(() => {
        this.refreshTokenPromise = undefined;
      });

    return this.refreshTokenPromise;
  }

  public get token() {
    return this.oauthService.getAccessToken();
  }

  public get isAuthenticated() {
    return this.validUser && this.oauthService.hasValidAccessToken();
  }

  public get claims() {
    return this.oauthService.getIdentityClaims();
  }

  public get validUser(): boolean {
    return this._validUser;
  }

  // The /profile response, cached from getProfile() alongside userRole. Consumers that
  // are not part of the ngrx-backed review state read it from here rather than the store.
  public get profile(): Profile | undefined {
    return this._profile;
  }

  public getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  public getUserDetails() {
    return this.oauthService.getIdentityClaims();
  }
  public getUserPreferredName(){
    return this.claims?.['preferred_username'];
  }

  getProfile(): Promise<void> {
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + this.getAccessToken()
    });

    return this.http.get(`${this.apiUrl}/profile`, { headers })
      .toPromise()
      .then((response: any) => {
        this.userRole = UserRole[response.who.title as keyof typeof UserRole];
        this.userPermissions = this.getUserPermissions();
        this._profile = response;
        this.store.dispatch(ReviewPageActions.addProfile({ profile: response }));
      })
      .catch(error => {
        console.error('Failed to load profile:', error);
      });
  }

  getUserPermissions(): string[] {
    const userPermissions: string[] = [];
    const userRolePermissions = rolePermissions[this.userRole];
    if (userRolePermissions) {
        userPermissions.push(...userRolePermissions);
    }
    return userPermissions;
  }

  hasPermission(permission: string): boolean {
    if (this.userPermissions.includes(permission)) {
        return true;
    }
    return false;
  }

  canAccessChartQueue(): boolean {
    return !AuthService.rolesBlockedFromChartQueue.has(this.userRole);
  }

  public invalidateUser(): void {
    if (this._validUser) {
      this._validUser = false;
    }
  }
}
